import { applicantFetch, clearApplicantSession, getApplicantAccessToken, isUnauthorizedResponse } from './applicantAuth';

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

class ApplicantApiError extends Error {
    constructor(message: string, public status: number, public isTokenExpired: boolean = false) {
        super(message);
        this.name = 'ApplicantApiError';
    }
}

const getApplicantToken = (): string | null => {
    return getApplicantAccessToken();
};

const handleApiResponse = async (response: Response) => {
    if (isUnauthorizedResponse(response)) {
        // Token expired/invalid or token is valid for a different role.
        clearApplicantSession();
        throw new ApplicantApiError('Authentication failed. Please log in again.', response.status, true);
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new ApplicantApiError(errorData.message || `HTTP ${response.status}`, response.status);
    }

    return response.json();
};

export const applicantApi = {
    // GET request
    get: async (endpoint: string) => {
        const token = getApplicantToken();
        if (!token) {
            throw new ApplicantApiError('No authentication token found', 401, true);
        }

        const response = await applicantFetch(`${VITE_API_BASE_URL}/applicant${endpoint}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });

        return handleApiResponse(response);
    },

    // POST request
    post: async (endpoint: string, data: any) => {
        const token = getApplicantToken();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const response = await applicantFetch(`${VITE_API_BASE_URL}/applicant${endpoint}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(data),
        });

        return handleApiResponse(response);
    },

    // PUT request  
    put: async (endpoint: string, data: any) => {
        const token = getApplicantToken();
        if (!token) {
            throw new ApplicantApiError('No authentication token found', 401, true);
        }

        const response = await applicantFetch(`${VITE_API_BASE_URL}/applicant${endpoint}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(data),
        });

        return handleApiResponse(response);
    },

    // DELETE request
    delete: async (endpoint: string) => {
        const token = getApplicantToken();
        if (!token) {
            throw new ApplicantApiError('No authentication token found', 401, true);
        }

        const response = await applicantFetch(`${VITE_API_BASE_URL}/applicant${endpoint}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });

        return handleApiResponse(response);
    }
};

export { ApplicantApiError };